import '../webpack-dynamic-config-change';
import React from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { Provider } from 'react-redux';
import { store } from 'reducers';
import Widgets from 'components/Widgets';
import ModalProvider from 'components/ui/Modals/ModalProvider';
import { initSentry, setUserContext as setSentryUserContext } from 'utils/sentry';
import { BrowserRouter } from 'react-router-dom';
import { BladeProvider, ToastContainer } from '@razorpay/blade/components';
import { bladeTheme } from '@razorpay/blade/tokens';
import { getThemePreference } from 'utils/getThemePreference';
import { WidgetThemeContextProvider } from 'WidgetThemeContextProvider';
import '@razorpay/blade/fonts.css';
import { RouterNavigationProvider } from 'context/RouterNavigationProvider';

initSentry();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      retryOnMount: false,
    },
  },
});

interface WidgetMessage {
  elementType: string;
  elementId: string;
  props: any;
}

declare global {
  interface Window {
    payroll: {
      setSentryUserContext: typeof setSentryUserContext;
      toggleLeftSidebar: () => void;
      isWidgetLoaded: boolean;
    };
  }
}

window.payroll = {
  setSentryUserContext,
  toggleLeftSidebar: () => {},
  isWidgetLoaded: true,
};

const roots = new Map<string, Root>();

window.addEventListener('message', (event) => {
  if (event.origin !== window.location.origin || !event.data.elementType) return;

  if (!event.data.elementId && !event.data.elementType) {
    return;
  }

  const { elementType, props, elementId }: WidgetMessage = event.data;

  const container = document.getElementById(elementId);
  if (container) {
    // createRoot must only be called once per container (React 18 requirement)
    if (!roots.has(elementId)) {
      roots.set(elementId, createRoot(container));
    }
    roots.get(elementId)!.render(
      <React.StrictMode>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <RouterNavigationProvider>
              <Provider store={store}>
                <WidgetThemeContextProvider initialTheme={getThemePreference()}>
                  <BladeProvider themeTokens={bladeTheme} colorScheme={getThemePreference()}>
                    <ToastContainer />
                    <ModalProvider>
                      <Widgets elementType={elementType} props={props} />
                    </ModalProvider>
                  </BladeProvider>
                </WidgetThemeContextProvider>
              </Provider>
            </RouterNavigationProvider>
          </BrowserRouter>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </React.StrictMode>,
    );
  }
});

window.postMessage({ message: 'payroll.widget.loaded' }, window.location.origin);
